import { composeThemeFromProps, Compose } from '@css-modules-theme/react';
import clsx from 'clsx';
import { forwardRef, useRef, useState, useMemo } from 'react';
import mergeRefs from 'react-merge-refs';
import { useUncontrolled } from '../../hooks/useUncontrolled.js';
import defaultStyles from './styles/Textarea.module.scss.js';
import { autoSizeTextareaHeight } from './utils/auto-size-height.js';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { isObject } from '../../utils/is.js';
import { useIsomorphicLayoutEffect } from '../../hooks/useIsomorphicLayoutEffect.js';
import { omit } from '../../utils/omit.js';
import { Enter } from '../../utils/keycode.js';

const ID_SUFFIX = '_textarea';
const Textarea = /*#__PURE__*/forwardRef((props, ref) => {
  const {
    theme,
    wrapperStyle,
    placeholder,
    disabled,
    validationStatus,
    maxLength,
    showWordLimit,
    dataTestId,
    name,
    onChange,
    defaultValue,
    value,
    label,
    labelPosition = 'inset',
    variant = 'default',
    showLabel = true,
    limitPosition = 'inset',
    limitLabel = 'Characters:',
    idSuffix = ID_SUFFIX,
    ...rest
  } = props;
  const styles = composeThemeFromProps(defaultStyles, {
    theme
  }, {
    compose: Compose.Merge
  });
  const trueMaxLength = isObject(maxLength) ? maxLength.length : maxLength;
  const mergedMaxLength = isObject(maxLength) && maxLength.errorOnly ? undefined : trueMaxLength;
  const hideLabel = showLabel === false || !label;
  const wrapperRef = useRef(null);
  const textareaRef = useRef();
  const [textAreaStyle, setTextAreaStyle] = useState({});
  const [focus, setFocus] = useState(false);
  const [_value, handleChange] = useUncontrolled({
    value,
    defaultValue,
    fallbackValue: '',
    onChange
  });
  if (disabled) {
    textAreaStyle.resize = 'none';
  }
  const handleOnChange = e => {
    const newValue = e.currentTarget.value;
    if (mergedMaxLength) {
      if (newValue.length <= trueMaxLength) {
        handleChange(newValue);
      }
    } else {
      handleChange(newValue);
    }
  };
  const resizeTextAreaHeight = () => {
    const taStyle = autoSizeTextareaHeight(props.autoSize, textareaRef.current);
    if (taStyle) {
      setTextAreaStyle(taStyle);
    }
  };
  useIsomorphicLayoutEffect(() => {
    resizeTextAreaHeight();
  }, [_value]);
  const valueLength = _value ? _value.length : 0;
  const lengthError = useMemo(() => {
    if (!mergedMaxLength && trueMaxLength) {
      return valueLength > trueMaxLength;
    }
    return false;
  }, [valueLength, trueMaxLength, mergedMaxLength]);
  const labelPositionClassNames = clsx({
    [styles.hidden]: hideLabel,
    [styles.label_inset]: labelPosition === 'inset',
    [styles.label_active]: labelPosition === 'inset' && (focus || _value)
  });
  const variantClassName = clsx({
    [styles.default]: variant === 'default',
    [styles.filled]: variant === 'filled',
    [styles.underlined]: variant === 'underlined'
  });
  const validationStatusClassName = clsx({
    [styles.success]: validationStatus === 'success',
    [styles.warning]: validationStatus === 'warning',
    [styles.error]: validationStatus === 'error' || lengthError
  });
  const wrapperClassNames = clsx(styles.root, {
    [styles.inset]: labelPosition === 'inset',
    [styles.focus]: focus,
    [styles.disabled]: disabled
  });
  const labelElement = /*#__PURE__*/jsx("label", {
    className: clsx(styles.label, labelPositionClassNames),
    htmlFor: name + idSuffix,
    "aria-labelledby": name + idSuffix,
    children: label ?? placeholder
  });
  const textAreaElement = /*#__PURE__*/jsx("span", {
    className: clsx(styles.textarea_wrapper, variantClassName, validationStatusClassName, {
      [styles.textarea_inset]: labelPosition === 'inset' && !hideLabel,
      [styles.disabled]: disabled
    }),
    children: /*#__PURE__*/jsx("textarea", {
      ...omit(rest, ['autoSize', 'defaultValue', 'onPressEnter']),
      maxLength: mergedMaxLength,
      ref: mergeRefs([textareaRef, ref])
      // style={{ ...textAreaStyle }}
      ,
      className: styles.textarea,
      placeholder: labelPosition !== 'inset' || !label ? placeholder : '',
      disabled: disabled,
      onChange: handleOnChange,
      onFocus: () => setFocus(true),
      onBlur: () => setFocus(false),
      onKeyDown: e => {
        const {
          onKeyDown,
          onPressEnter
        } = props;
        onKeyDown && onKeyDown(e);
        if (e.code === Enter.key) {
          onPressEnter && onPressEnter(e);
        }
      },
      value: _value || '',
      "data-test-id": dataTestId,
      id: name + idSuffix
    })
  });
  const defaultInput = /*#__PURE__*/jsxs(Fragment, {
    children: [labelElement, textAreaElement]
  });
  return /*#__PURE__*/jsxs("div", {
    className: wrapperClassNames,
    style: wrapperStyle,
    ref: wrapperRef,
    onFocus: () => {
      textareaRef.current && textareaRef.current.focus && textareaRef.current.focus();
    },
    onBlur: () => {
      textareaRef.current && textareaRef.current.blur && textareaRef.current.blur();
    },
    children: [defaultInput, !!trueMaxLength && showWordLimit && /*#__PURE__*/jsxs("span", {
      className: clsx(styles.limit, {
        [styles.limit_inset]: limitPosition === 'inset',
        [styles.limit_error]: lengthError
      }),
      children: [limitLabel, " ", valueLength, "/", trueMaxLength]
    })]
  });
});
Textarea.displayName = 'Textarea';

export { Textarea };
